import { db } from '@/lib/firebase';
import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc, deleteDoc,
  query, where, orderBy, limit, Timestamp, addDoc, arrayUnion, arrayRemove,
  onSnapshot,
} from 'firebase/firestore';

// ─── Types ───────────────────────────────────────────────────────────
export interface MediaCredit {
  id?: string;
  mediaId: string;          // release or work ID
  mediaType: 'release' | 'work' | 'video'; // what type of media
  mediaTitle: string;
  userId: string;           // the user being credited
  userName: string;
  userAvatar?: string;
  role: string;             // e.g., 'Producer', 'Writer', etc.
  splitPercent?: number;    // percentage of rights 0-100
  status: 'pending' | 'approved' | 'rejected' | 'disputed';
  claimedBy: string;        // who made the claim (user themselves or admin)
  claimedByName: string;
  approvedBy?: string;      // the credited user who approved
  approvedAt?: Timestamp;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  notes?: string;
}

export interface CreditDispute {
  id?: string;
  creditId: string;         // which credit is being disputed
  mediaId: string;
  disputedBy: string;       // user ID of person disputing
  disputedByName: string;
  reason: string;           // explanation of the dispute
  proposedRole?: string;
  proposedSplitPercent?: number;
  status: 'open' | 'resolved' | 'rejected';
  resolution?: string;
  resolvedBy?: string;
  resolvedAt?: Timestamp;
  comments: DisputeComment[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface DisputeComment {
  id: string;
  userId: string;
  userName: string;
  userAvatar?: string;
  message: string;
  createdAt: Timestamp;
}

export interface LyricsData {
  id?: string;
  mediaId: string;
  mediaTitle: string;
  lyrics: string;
  /** Optional LRC-style per-line timings for exact karaoke sync. Each entry is
   *  the start time (seconds) of a sung line. Absent → fall back to estimate. */
  synced?: { t: number; text: string }[];
  isAiGenerated: boolean;
  aiConfidence?: number;   // 0-1 confidence score
  status: 'draft' | 'pending_review' | 'approved' | 'rejected';
  submittedBy: string;
  submittedByName: string;
  approvedBy?: string;
  approvedAt?: Timestamp;
  corrections: LyricsCorrection[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface LyricsCorrection {
  id: string;
  userId: string;
  userName: string;
  originalText: string;
  correctedText: string;
  lineNumber?: number;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: Timestamp;
}

// ─── Credit CRUD ─────────────────────────────────────────────────────

/** Claim a credit on a media file */
export async function claimCredit(data: Omit<MediaCredit, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
  const now = Timestamp.now();
  // Strip undefined values — Firestore rejects documents with undefined fields
  const safe: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(data)) {
    if (v !== undefined) safe[k] = v;
  }
  const ref = await addDoc(collection(db, 'mediaCredits'), {
    ...safe,
    createdAt: now,
    updatedAt: now,
  });
  return ref.id;
}

/** Get all credits for a media file */
export async function getCreditsForMedia(mediaId: string): Promise<MediaCredit[]> {
  const q = query(
    collection(db, 'mediaCredits'),
    where('mediaId', '==', mediaId),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as MediaCredit));
}

/** Get all credits for a user */
export async function getCreditsForUser(userId: string): Promise<MediaCredit[]> {
  const q = query(
    collection(db, 'mediaCredits'),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as MediaCredit));
}

/** Get pending credits that need approval from a user */
export async function getPendingCreditsForUser(userId: string): Promise<MediaCredit[]> {
  const q = query(
    collection(db, 'mediaCredits'),
    where('userId', '==', userId),
    where('status', '==', 'pending'),
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as MediaCredit));
}

/** Approve a credit (the credited user confirms their role) */
export async function approveCredit(creditId: string, approvedByUserId: string): Promise<void> {
  const ref = doc(db, 'mediaCredits', creditId);
  await updateDoc(ref, {
    status: 'approved',
    approvedBy: approvedByUserId,
    approvedAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });
}

/** Reject a credit */
export async function rejectCredit(creditId: string): Promise<void> {
  const ref = doc(db, 'mediaCredits', creditId);
  await updateDoc(ref, {
    status: 'rejected',
    updatedAt: Timestamp.now(),
  });
}

/** Update credit details (admin or claimant) */
export async function updateCredit(creditId: string, updates: Partial<MediaCredit>): Promise<void> {
  const ref = doc(db, 'mediaCredits', creditId);
  await updateDoc(ref, {
    ...updates,
    updatedAt: Timestamp.now(),
  });
}

/** Delete a credit */
export async function deleteCredit(creditId: string): Promise<void> {
  await deleteDoc(doc(db, 'mediaCredits', creditId));
}

/** Admin: Attach a user to a media file */
export async function adminAttachUserToMedia(
  mediaId: string, mediaType: 'release' | 'work' | 'video', mediaTitle: string,
  userId: string, userName: string, userAvatar: string | undefined,
  role: string, splitPercent: number | undefined,
  adminId: string, adminName: string,
): Promise<string> {
  return claimCredit({
    mediaId,
    mediaType,
    mediaTitle,
    userId,
    userName,
    userAvatar,
    role,
    splitPercent,
    status: 'pending', // User still needs to approve
    claimedBy: adminId,
    claimedByName: adminName,
  });
}

// ─── Dispute CRUD ────────────────────────────────────────────────────

/** Create a dispute on a credit */
export async function createDispute(data: Omit<CreditDispute, 'id' | 'createdAt' | 'updatedAt' | 'comments'>): Promise<string> {
  const now = Timestamp.now();
  const ref = await addDoc(collection(db, 'creditDisputes'), {
    ...data,
    comments: [],
    createdAt: now,
    updatedAt: now,
  });
  // Mark the credit as disputed
  const creditRef = doc(db, 'mediaCredits', data.creditId);
  await updateDoc(creditRef, { status: 'disputed', updatedAt: now });
  return ref.id;
}

/** Get disputes for a media file */
export async function getDisputesForMedia(mediaId: string): Promise<CreditDispute[]> {
  const q = query(
    collection(db, 'creditDisputes'),
    where('mediaId', '==', mediaId),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as CreditDispute));
}

/** Get disputes filed by a user */
export async function getDisputesByUser(userId: string): Promise<CreditDispute[]> {
  const q = query(
    collection(db, 'creditDisputes'),
    where('disputedBy', '==', userId),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as CreditDispute));
}

/** Add a comment to a dispute */
export async function addDisputeComment(
  disputeId: string,
  userId: string, userName: string, userAvatar: string | undefined,
  message: string,
): Promise<void> {
  const ref = doc(db, 'creditDisputes', disputeId);
  const comment: DisputeComment = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    userId,
    userName,
    userAvatar,
    message,
    createdAt: Timestamp.now(),
  };
  await updateDoc(ref, {
    comments: arrayUnion(comment),
    updatedAt: Timestamp.now(),
  });
}

/** Resolve a dispute */
export async function resolveDispute(
  disputeId: string,
  resolution: string,
  resolvedBy: string,
  newStatus: 'resolved' | 'rejected' = 'resolved',
): Promise<void> {
  const ref = doc(db, 'creditDisputes', disputeId);
  await updateDoc(ref, {
    status: newStatus,
    resolution,
    resolvedBy,
    resolvedAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });
}

// ─── Lyrics CRUD ─────────────────────────────────────────────────────

/** Save AI-generated or user-submitted lyrics */
export async function saveLyrics(data: Omit<LyricsData, 'id' | 'createdAt' | 'updatedAt' | 'corrections'>): Promise<string> {
  const now = Timestamp.now();
  // Check if lyrics already exist for this media
  const existing = await getLyricsForMedia(data.mediaId);
  if (existing) {
    await updateDoc(doc(db, 'mediaLyrics', existing.id!), {
      lyrics: data.lyrics,
      ...(data.synced !== undefined ? { synced: data.synced } : {}),
      isAiGenerated: data.isAiGenerated,
      aiConfidence: data.aiConfidence,
      status: data.status,
      submittedBy: data.submittedBy,
      submittedByName: data.submittedByName,
      updatedAt: now,
    });
    return existing.id!;
  }
  const ref = await addDoc(collection(db, 'mediaLyrics'), {
    ...data,
    corrections: [],
    createdAt: now,
    updatedAt: now,
  });
  return ref.id;
}

/** Get lyrics for a media file */
export async function getLyricsForMedia(mediaId: string): Promise<LyricsData | null> {
  const q = query(
    collection(db, 'mediaLyrics'),
    where('mediaId', '==', mediaId),
    limit(1)
  );
  const snap = await getDocs(q);
  if (!snap.empty) return { id: snap.docs[0].id, ...snap.docs[0].data() } as LyricsData;

  // Fallback: lyrics entered in the Media Vault edit form are saved on the
  // work doc (works/{id}.lyrics), NOT in the mediaLyrics collection — so the
  // player showed "No lyrics available" for tracks whose lyrics were typed/
  // pasted in the vault. Read them straight off the work.
  try {
    const wSnap = await getDoc(doc(db, 'works', mediaId));
    const w = wSnap.exists() ? (wSnap.data() as any) : null;
    const raw = (w?.lyrics ?? '').toString();
    if (raw.trim()) {
      return {
        id: '',
        mediaId,
        lyrics: raw,
        isAiGenerated: false,
        aiConfidence: 1,
        status: 'approved',
        corrections: [],
      } as unknown as LyricsData;
    }
  } catch { /* ignore — no work doc / permission */ }
  return null;
}

/** Submit a lyrics correction */
export async function submitLyricsCorrection(
  lyricsId: string,
  userId: string, userName: string,
  originalText: string, correctedText: string,
  lineNumber?: number,
): Promise<void> {
  const correction: LyricsCorrection = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    userId,
    userName,
    originalText,
    correctedText,
    lineNumber,
    status: 'pending',
    createdAt: Timestamp.now(),
  };
  const ref = doc(db, 'mediaLyrics', lyricsId);
  await updateDoc(ref, {
    corrections: arrayUnion(correction),
    updatedAt: Timestamp.now(),
  });
}

/** Approve a lyrics correction */
export async function approveLyricsCorrection(lyricsId: string, correctionId: string): Promise<void> {
  const ref = doc(db, 'mediaLyrics', lyricsId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const data = snap.data() as LyricsData;
  const corrections = data.corrections || [];
  const correctionIdx = corrections.findIndex(c => c.id === correctionId);
  if (correctionIdx < 0) return;

  const correction = corrections[correctionIdx];
  // Apply the correction to the lyrics
  const updatedLyrics = data.lyrics.replace(correction.originalText, correction.correctedText);
  corrections[correctionIdx] = { ...correction, status: 'approved' };

  await updateDoc(ref, {
    lyrics: updatedLyrics,
    corrections,
    updatedAt: Timestamp.now(),
  });
}

/** Listen to credits for a media file in real-time */
export function subscribeToMediaCredits(
  mediaId: string,
  callback: (credits: MediaCredit[]) => void
): () => void {
  const q = query(
    collection(db, 'mediaCredits'),
    where('mediaId', '==', mediaId),
    orderBy('createdAt', 'desc')
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() } as MediaCredit)));
  });
}

/** Listen to disputes for a media file in real-time */
export function subscribeToMediaDisputes(
  mediaId: string,
  callback: (disputes: CreditDispute[]) => void
): () => void {
  const q = query(
    collection(db, 'creditDisputes'),
    where('mediaId', '==', mediaId),
    orderBy('createdAt', 'desc')
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() } as CreditDispute)));
  });
}
